import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import { categories } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL;

const webpageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'How to Use Pandabuy Spreadsheet: A Complete Shopping Guide',
  description:
    'Master the art of discovering high-quality products through curated pandabuy spreadsheets. This step-by-step guide walks you through everything from category navigation to spotting the best deals.',
  url: `${siteUrl}/shopping-guide`,
  inLanguage: 'en',
  isPartOf: {
    '@type': 'WebSite',
    name: 'pandabuy.monster',
    url: siteUrl,
  },
};

export default function GuidePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageJsonLd) }}
      />
      <Navbar />
      <article className="pt-20">
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-start">
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4 whitespace-nowrap">
                  Shopping Guide
                </span>
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-foreground-950 leading-tight mb-6">
                  How to Use Pandabuy<br />Spreadsheet: A Complete<br />Shopping Guide
                </h1>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-lg mb-6">
                  Master the art of discovering high-quality products through curated pandabuy spreadsheets. This step-by-step guide walks you through everything from category navigation to spotting the best deals.
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Open Pandabuy Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
              <div className="flex-1">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20beautifully%20organized%20desk%20setup%20with%20laptop%20showing%20spreadsheet%20interface%20alongside%20stylish%20fashion%20items%20shoes%20and%20accessories%20on%20warm%20natural%20wood%20surface%2C%20editorial%20photography%20with%20soft%20afternoon%20sunlight%2C%20clean%20minimalist%20aesthetic%2C%20warm%20earthy%20tones%2C%20productive%20lifestyle%20composition&width=700&height=500&seq=guide-hero-shopping&orientation=landscape"
                  alt="Pandabuy Spreadsheet Shopping Guide"
                  className="w-full rounded-2xl object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6">
              Step 1: Choose Your Category Wisely
            </h2>
            <p className="text-foreground-700 leading-relaxed mb-6 text-sm md:text-base">
              The first and most important decision when using a pandabuy spreadsheet is selecting the right category to explore. Each category functions as an independently curated collection with its own quality standards, community reviewers, and update schedule. Whether you are hunting for sneakers, streetwear, luxury bags, or accessories, starting in the right category dramatically improves your discovery efficiency.
            </p>
            <p className="text-foreground-700 leading-relaxed mb-8 text-sm md:text-base">
              Take a moment to browse through the available categories listed below. Each one opens a dedicated spreadsheet section with verified listings, quality ratings, and detailed buyer notes. Do not try to explore everything at once — pick one category that genuinely interests you and dive deep.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-10">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-4 py-3 rounded-lg bg-background-100 border border-background-200 hover:border-accent-300 transition-all text-sm text-foreground-700 hover:text-foreground-950 whitespace-nowrap cursor-pointer"
                >
                  <img src={cat.image} alt={cat.name} className="w-8 h-8 rounded object-cover" />
                  {cat.name}
                </a>
              ))}
            </div>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6 mt-12">
              Step 2: Learn to Read Quality Ratings
            </h2>
            <p className="text-foreground-700 leading-relaxed mb-6 text-sm md:text-base">
              Quality ratings are the backbone of any pandabuy spreadsheet. These community-generated scores provide a quick reference for how a particular listing compares to expectations. Ratings of 4.5 and above indicate exceptional quality that meets or exceeds what experienced buyers expect. Ratings between 3.5 and 4.5 represent solid value with minor trade-offs. Anything below 3.0 should be approached with careful scrutiny.
            </p>
            <p className="text-foreground-700 leading-relaxed mb-6 text-sm md:text-base">
              Beyond the numerical score, always read the detailed buyer notes. These often contain crucial context — perhaps a 4.2-rated item has outstanding materials but slightly off sizing, or a 3.8-rated piece delivers exceptional value if you are willing to overlook minor cosmetic details. The numbers tell part of the story, but the community notes complete it.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6 mt-12">
              Step 3: Compare Before You Commit
            </h2>
            <p className="text-foreground-700 leading-relaxed mb-6 text-sm md:text-base">
              One of the greatest strengths of the pandabuy spreadsheet format is the ability to compare similar items side by side. When you find a listing that catches your eye, resist the urge to act immediately. Instead, browse other entries in the same category at similar price points. You will often discover that a slightly higher-rated option exists at a comparable price, or that a more affordable alternative delivers nearly identical quality.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6 mt-12">
              Step 4: Check Update Freshness
            </h2>
            <p className="text-foreground-700 leading-relaxed mb-6 text-sm md:text-base">
              Pandabuy spreadsheets are living documents that thrive on regular updates. Before investing significant time in a particular spreadsheet section, verify its last update date. Freshly updated spreadsheets contain the most relevant listings with current pricing and availability information. Sections that have not been refreshed in weeks may contain outdated or sold-out listings that waste your time.
            </p>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6 mt-12">
              Step 5: Build Relationships With Trusted Reviewers
            </h2>
            <p className="text-foreground-700 leading-relaxed mb-8 text-sm md:text-base">
              Over time, you will begin to recognize community reviewers whose judgment consistently aligns with your personal preferences. Pay attention to whose detailed notes you find most helpful and whose quality assessments most reliably predict your own satisfaction. These trusted voices become invaluable guides as you explore new categories and expand your spreadsheet discovery horizons.
            </p>

            <div className="border-t border-background-200 pt-8 mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-foreground-950 text-white text-sm font-medium hover:bg-foreground-900 transition-all whitespace-nowrap cursor-pointer"
              >
                BUY IT NOW
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </section>
      </article>
      <Footer />
    </main>
  );
}